import React, { useEffect, useRef, useState } from 'react';
import { useEZOPENAudio } from '../../hooks/useEZOPENAudio';
import AudioControls from './AudioControls';
import EZOPENControls from './EZOPENControls';
import CameraIntercomControl, { cameraIntercomSupport } from '../cameras/CameraIntercomControl';
import { destroyEZOPENPlugin, waitForEZOPENCleanup } from '../../utils/ezopenLifecycle';
import { playbackErrorMessage, playbackMillis, recordingToken, recorderWallTime, playbackWallTimeAt, toPlaybackWallTime } from '../../utils/ezopenPlayback';

// Declare global types for EZOPEN plugin
declare global {
  interface Window {
    JSPlugin: any;
    TimeBar: any;
    __EZOPEN_SCRIPTS_LOADING__?: boolean;
    __EZOPEN_SCRIPTS_LOADED__?: boolean;
    __EZOPEN_LOAD_PROMISE__?: Promise<void>;
  }
}

// Global script loader - only loads once for all player instances
const loadEZOPENScripts = (): Promise<void> => {
  // Return existing promise if already loading
  if (window.__EZOPEN_LOAD_PROMISE__) {
    return window.__EZOPEN_LOAD_PROMISE__;
  }

  // Return resolved promise if already loaded
  if (window.__EZOPEN_SCRIPTS_LOADED__ && window.JSPlugin && window.TimeBar) {
    return Promise.resolve();
  }

  // Mark as loading
  window.__EZOPEN_SCRIPTS_LOADING__ = true;

  const loadScript = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement;
      if (existing) {
        // Script already in DOM - check if it has a data-loaded attribute
        if (existing.dataset.loaded === 'true') {
          resolve();
          return;
        }
        // Script exists but may not be loaded yet - wait for it
        existing.onload = () => {
          existing.dataset.loaded = 'true';
          resolve();
        };
        existing.onerror = () => {
          existing.remove();
          reject(new Error(`Failed to load ${src}`));
        };
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = false; // Load in order
      script.onload = () => {
        script.dataset.loaded = 'true';
        resolve();
      };
      script.onerror = () => {
        script.remove();
        reject(new Error(`Failed to load ${src}`));
      };
      document.head.appendChild(script);
    });
  };

  window.__EZOPEN_LOAD_PROMISE__ = (async () => {
    try {
      // Load scripts in sequence
      await loadScript('/ezopen/jsPlugin-3.0.0.min.js');
      await loadScript('/ezopen/timeBar.js');
      
      // Wait a bit for scripts to initialize
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Verify JSPlugin is available
      if (!window.JSPlugin || !window.TimeBar) {
        throw new Error('JSPlugin or TimeBar not initialized after loading');
      }
      
      window.__EZOPEN_SCRIPTS_LOADED__ = true;
      window.__EZOPEN_SCRIPTS_LOADING__ = false;
    } catch (err) {
      window.__EZOPEN_SCRIPTS_LOADING__ = false;
      window.__EZOPEN_LOAD_PROMISE__ = undefined;
      window.__EZOPEN_SCRIPTS_LOADED__ = false;
      console.error('❌ Failed to load EZOPEN scripts:', err);
      throw err;
    }
  })();

  return window.__EZOPEN_LOAD_PROMISE__;
};

interface EZOPENPlayerProps {
  camera: {
    id: number;
    cloud_serial_no?: string;
    cloud_channel_no?: string;
    cloud_provider?: string;
    capabilities?: unknown;
  };
  mode?: 'live' | 'playback';
  startTime?: string; // ISO 8601 for playback
  endTime?: string; // ISO 8601 for playback
  searchStartTime?: string; // Explicit UTC instant for provider recording search
  searchEndTime?: string;
  playbackTimezone?: string | null;
  initialStorageType?: 1 | 2;
  eventTime?: string; // Recorder wall time, for the event marker and jump action
  width?: number;
  height?: number;
  showControls?: boolean;  // Show playback controls
  showAudioControls?: boolean;  // Show audio controls (listen/talk)
  showIntercom?: boolean;
  audioControlsSize?: 'sm' | 'md' | 'lg';  // Size of audio controls
  showAudioWaveform?: boolean;  // Show audio waveform visualization
  autoPlayDelay?: number;  // Delay in ms before auto-playing (for staggering multiple players)
  fillContainer?: boolean;  // Fill parent container (no max-width constraint)
  videoFit?: 'contain' | 'fill'; // Fullscreen walls can fill tiles without cropping.
  streamQuality?: 'hd' | 'sub';
  cameraName?: string;
  onToggleFullscreen?: () => void;
  onReady?: () => void;
  onError?: (error: any) => void;
}

export interface EZOPENPlayerHandle {
  captureSnapshot: () => Promise<Blob>;
}

const EZOPENPlayer = React.forwardRef<EZOPENPlayerHandle, EZOPENPlayerProps>(({
  camera,
  mode = 'live',
  startTime,
  endTime,
  searchStartTime,
  searchEndTime,
  playbackTimezone,
  initialStorageType,
  eventTime,
  width = 800,
  height = 450,
  showControls = true,
  showAudioControls = false,
  showIntercom = false,
  audioControlsSize = 'md',
  showAudioWaveform = false,
  autoPlayDelay = 0,
  fillContainer = false,
  videoFit = 'contain',
  streamQuality = 'hd',
  cameraName,
  onToggleFullscreen,
  onReady,
  onError
}, ref) => {
  const playerRef = useRef<HTMLDivElement>(null);
  const pluginRef = useRef<any>(null);
  const playerIdRef = useRef(`playWind_${camera.id}_${Math.random().toString(36).substr(2, 9)}`);
  const lastDimensionsRef = useRef<{ width: number; height: number } | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  
  // Lifecycle management
  type LifecycleState = 'idle' | 'initializing' | 'playing' | 'stopping' | 'destroyed' | 'error';
  const lifecycleStateRef = useRef<LifecycleState>('idle');
  const setLifecycleState = (state: LifecycleState) => {
    lifecycleStateRef.current = state;
  };
  const lifecycleGenerationRef = useRef(0); // Increment to invalidate stale async operations
  React.useImperativeHandle(ref, () => ({
    captureSnapshot: () => {
      const plugin = pluginRef.current;
      const generation = lifecycleGenerationRef.current;
      if (!plugin || lifecycleStateRef.current !== 'playing' || typeof plugin.JS_CapturePicture !== 'function') {
        return Promise.reject(new Error('Wait for the live camera picture before capturing.'));
      }
      // The bundled SDK renders through WebGL/canvas as well as video. Its
      // capture callback supplies JPEG bytes without downloading a local file.
      return new Promise<Blob>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('Camera capture timed out. Retry the camera connection.')), 10000);
        const fail = (error: unknown) => {
          clearTimeout(timer);
          reject(error instanceof Error ? error : new Error('The camera could not capture a picture.'));
        };
        try {
          Promise.resolve(plugin.JS_CapturePicture(0, 'entry-exit-photo', 'JPEG', (bytes: Uint8Array) => {
            clearTimeout(timer);
            if (pluginRef.current !== plugin || lifecycleGenerationRef.current !== generation) {
              reject(new Error('The selected camera changed. Capture the photo again.'));
            } else if (!bytes?.byteLength) {
              reject(new Error('The camera returned an empty picture.'));
            } else {
              resolve(new Blob([new Uint8Array(bytes)], { type: 'image/jpeg' }));
            }
          })).catch(fail);
        } catch (error) { fail(error); }
      });
    },
  }), []);
  const activeTimersRef = useRef<Set<NodeJS.Timeout>>(new Set()); // Track all active timers
  const activeDelaysRef = useRef<Map<NodeJS.Timeout, () => void>>(new Map());
  const callbacksRef = useRef({ onReady, onError });
  callbacksRef.current = { onReady, onError };
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const [cloudTokens, setCloudTokens] = useState<any>(null);
  const [scriptsLoaded, setScriptsLoaded] = useState(window.__EZOPEN_SCRIPTS_LOADED__ || false);
  const [currentMode, setCurrentMode] = useState<'live' | 'playback'>(mode);
  const [isPlaying, setIsPlaying] = useState(false);
  const [intercomInUse, setIntercomInUse] = useState(false);
  
  // Audio controls hook
  // Only initialize audio controls if enabled and camera has cloud support
  const shouldEnableAudio = showAudioControls && 
    Boolean(camera.cloud_provider && camera.cloud_serial_no && camera.cloud_channel_no);
  
  const audioControls = useEZOPENAudio({
    cameraId: camera.id,
    cloudSerialNo: camera.cloud_serial_no,
    cloudChannelNo: camera.cloud_channel_no,
    cloudProvider: camera.cloud_provider
  });
  
  const [playbackStart, setPlaybackStart] = useState<string>(() => {
    // Use provided startTime if available, otherwise default to 1 hour ago
    if (startTime) {
      return toPlaybackWallTime(startTime);
    }
    const now = new Date();
    now.setHours(now.getHours() - 1);
    return recorderWallTime(now, playbackTimezone);
  });
  const [playbackEnd, setPlaybackEnd] = useState<string>(() => {
    // Use provided endTime if available, otherwise default to now
    if (endTime) {
      return toPlaybackWallTime(endTime);
    }
    const now = new Date();
    return recorderWallTime(now, playbackTimezone);
  });
  // Default to local storage (2) for event playback, cloud (1) for manual playback
  const [storageType, setStorageType] = useState<1 | 2>(
    initialStorageType ?? ((startTime && endTime) ? 2 : 1)
  );
  const playbackSelectionRef = useRef({ start: playbackStart, end: playbackEnd, storageType });
  playbackSelectionRef.current = { start: playbackStart, end: playbackEnd, storageType };
  const reconnectTimerRef = useRef<any>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectingRef = useRef(false);
  const livePlayOperationRef = useRef<{ generation: number; promise: Promise<void> } | null>(null);
  const maxReconnectAttempts = 5;
  const progressTimerRef = useRef<any>(null); // For smooth timeline progress updates
  const lastPlayParamsRef = useRef<{mode: 'live' | 'playback', params?: any}>(null);
  const audioOpenedRef = useRef(false); // Track if JS_OpenSound has been called
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Timeline progress tracking (0-100 percentage)
  const [playbackProgress, setPlaybackProgress] = useState<number>(0);
  const playbackProgressRef = useRef(0);
  const progressEpochRef = useRef(0);
  const updatePlaybackProgress = (value: number) => { playbackProgressRef.current = value; setPlaybackProgress(value); };
  const [timelineHoverPercent, setTimelineHoverPercent] = useState<number | null>(null);
  // Auto-hide controls in fullscreen
  const [showFullscreenControls, setShowFullscreenControls] = useState(true);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const controlsHoveredRef = useRef(false);
  const controlsFocusedRef = useRef(false);
  
  // Detect auto-play mode (event playback) - hide mode toggle and settings
  const isAutoPlayMode = !!(startTime && endTime);

  // ========== LIFECYCLE MANAGEMENT HELPERS ==========
  
  // Create a tracked timer that will be auto-cancelled on cleanup
  const createTrackedTimer = (callback: () => void, delay: number): NodeJS.Timeout => {
    const timer = setTimeout(() => {
      activeTimersRef.current.delete(timer);
      callback();
    }, delay);
    activeTimersRef.current.add(timer);
    return timer;
  };

  const waitForDelay = (delay: number): Promise<void> => new Promise(resolve => {
    const timer = createTrackedTimer(() => {
      activeDelaysRef.current.delete(timer);
      resolve();
    }, delay);
    activeDelaysRef.current.set(timer, resolve);
  });

  // Cancel all active timers
  const cancelAllTimers = () => {
    activeTimersRef.current.forEach(timer => clearTimeout(timer));
    activeTimersRef.current.clear();
    // Settle cancelled waits so their async functions can exit and release refs.
    activeDelaysRef.current.forEach(resolve => resolve());
    activeDelaysRef.current.clear();
    
    // Also cancel specific tracked timers
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  // Check if current lifecycle generation is still valid
  const isCurrentGeneration = (generation: number): boolean => {
    return generation === lifecycleGenerationRef.current && lifecycleStateRef.current !== 'destroyed';
  };

  // Diagnostic logging with player context
  const logPlayerEvent = (event: string, data?: any) => {
    console.log(`[Player ${camera.id}] [${lifecycleStateRef.current}] [Gen ${lifecycleGenerationRef.current}] ${event}`, data || '');
  };

  // ========== END LIFECYCLE HELPERS ==========

  // Handle mouse activity for auto-hiding controls
  const resetInactivityTimer = () => {
    // Show controls immediately
    setShowFullscreenControls(true);
    
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    // Set new timer to hide controls after 5 seconds
    if (isFullscreen || fillContainer) {
      inactivityTimerRef.current = setTimeout(() => {
        if (!controlsHoveredRef.current && !controlsFocusedRef.current) setShowFullscreenControls(false);
      }, 5000);
    }
  };

  // Clean up timer on unmount or when leaving fullscreen
  useEffect(() => {
    if (!(isFullscreen || fillContainer)) {
      setShowFullscreenControls(true);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    } else {
      // Start timer when entering fullscreen
      resetInactivityTimer();
    }
    
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [isFullscreen, fillContainer]);

  // Coalesce SDK error bursts into one reconnect operation per generation.
  const attemptReconnect = useRef(async () => {});
  attemptReconnect.current = async () => {
    const generation = lifecycleGenerationRef.current;
    if (!isCurrentGeneration(generation) || reconnectTimerRef.current || reconnectingRef.current) return;

    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      setError('Connection lost. Please try playing again.');
      setIsPlaying(false);
      setLifecycleState('error');
      return;
    }

    reconnectAttemptsRef.current += 1;
    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 10000);
    reconnectTimerRef.current = createTrackedTimer(async () => {
      reconnectTimerRef.current = null;
      if (!isCurrentGeneration(generation) || !lastPlayParamsRef.current || !pluginRef.current) return;
      reconnectingRef.current = true;
      let retry = false;
      try {
        setError(null);
        setLifecycleState('initializing');
        if (lastPlayParamsRef.current.mode === 'live') {
          await playLiveStream();
        } else {
          await playRecording();
        }
        if (!isCurrentGeneration(generation)) return;
        reconnectAttemptsRef.current = 0;
        setIsPlaying(true);
        setLifecycleState('playing');
        if (lastPlayParamsRef.current.mode === 'playback' && lastPlayParamsRef.current.params) {
          startProgressTimer.current(lastPlayParamsRef.current.params.startTime, lastPlayParamsRef.current.params.endTime);
        }
      } catch (err: any) {
        if (!isCurrentGeneration(generation)) return;
        setError(err?.message || 'Reconnection failed');
        setLifecycleState('error');
        retry = true;
      } finally {
        if (isCurrentGeneration(generation)) {
          reconnectingRef.current = false;
          if (retry) void attemptReconnect.current();
        }
      }
    }, delay);
  };

  // Handle WebSocket errors and trigger reconnection (updated every render)
  const handleWebSocketError = useRef((errorCode: number) => {});
  handleWebSocketError.current = (errorCode: number) => {
    if (lifecycleStateRef.current === 'destroyed') {
      logPlayerEvent('WebSocket error ignored - player destroyed', { errorCode });
      return;
    }
    
    if (errorCode === 1006) {
      logPlayerEvent('WebSocket connection closed abnormally (1006) - attempting reconnection');
      
      // Stop recording if it was active
      if (isRecording) {
        setIsRecording(false);
      }
      
      setError('Connection lost - reconnecting...');
      setIsPlaying(false);
      setLifecycleState('error');
      stopProgressTimer.current();
      attemptReconnect.current();
    }
  };

  // Poll recorder wall time, retaining the current position across pause/seek.
  const startProgressTimer = useRef((startTimeStr: string, endTimeStr: string) => {
    const plugin = pluginRef.current;
    const generation = lifecycleGenerationRef.current;
    if (!plugin || !isCurrentGeneration(generation)) return;
    const epoch = ++progressEpochRef.current;
    const isActive = () => plugin && pluginRef.current === plugin && isCurrentGeneration(generation) && progressEpochRef.current === epoch;
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    const startTimeMs = playbackMillis(startTimeStr);
    const endTimeMs = playbackMillis(endTimeStr);
    const duration = endTimeMs - startTimeMs;
    if (!(duration > 0)) return;
    const baseProgress = playbackProgressRef.current;
    const began = Date.now();
    let polling = false;
    const updateProgress = async () => {
      if (!isActive() || polling) return;
      polling = true;
      try {
        // The bundled SDK exposes the recorder's clock as separate fields.
        const frame = await plugin.JS_GetOSDTime(0, 'standard');
        if (!isActive()) return;
        if (!frame?.year) throw new Error('Recording clock unavailable');
        const osdTime = Date.UTC(frame.year, frame.month - 1, frame.day, frame.hour, frame.minute, frame.second, frame.milliSecond || 0);
        updatePlaybackProgress(Math.min(100, Math.max(0, (osdTime - startTimeMs) / duration * 100)));
      } catch {
        if (!isActive()) return;
        updatePlaybackProgress(Math.min(100, baseProgress + (Date.now() - began) / duration * 100));
      } finally { polling = false; }
      if (isActive() && playbackProgressRef.current >= 100) {
        stopProgressTimer.current();
        setIsPlaying(false); setIsPaused(false); setLifecycleState('idle');
        try { await plugin.JS_Stop(0); } catch { /* Already stopped by the SDK. */ }
      }
    };
    createTrackedTimer(updateProgress, 100);
    progressTimerRef.current = setInterval(updateProgress, 1000);
  });

  const stopProgressTimer = useRef(() => {});
  stopProgressTimer.current = () => {
    progressEpochRef.current += 1;
    if (progressTimerRef.current) { clearInterval(progressTimerRef.current); progressTimerRef.current = null; }
  };

  // ========== REMOVED GLOBAL ERROR HANDLER ==========
  // The global window.unhandledrejection listener has been removed because:
  // 1. It caused errors in Camera A to display in all Cameras B-P
  // 2. It globally suppressed errors with event.preventDefault()
  // 3. It couldn't reliably determine which player instance caused the error
  // 
  // Error handling now uses:
  // - SDK-specific onPluginEventHandler callback
  // - Try-catch blocks around all SDK method calls
  // - Player-specific error state management
  // ====================================================

  // Load EZOPEN scripts using global loader
  useEffect(() => {
    let mounted = true;

    const initScripts = async () => {
      try {
        // Use global loader to ensure scripts load only once
        await loadEZOPENScripts();
        
        if (mounted) {
          setScriptsLoaded(true);
        }
      } catch (err) {
        console.error('❌ Failed to load EZOPEN scripts:', err);
        if (mounted) {
          // Only set error after scripts fail to load, not during loading
          setError('Failed to load the video engine. Retry the connection.');
          setLoading(false);
          callbacksRef.current.onError?.(err);
        }
      }
    };

    initScripts();

    return () => {
      mounted = false;
    };
  }, [connectionAttempt]);

  // Track fullscreen controls; ResizeObserver handles the SDK dimensions.
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || 
        (document as any).webkitFullscreenElement || 
        (document as any).mozFullScreenElement || 
        (document as any).msFullscreenElement;
      
      const playerWrapper = playerRef.current?.closest('.ezopen-player-wrapper');
      // A fullscreen grid contains several players. Only an individual player
      // should switch its controls to the single-camera fullscreen layout.
      const isOurPlayerFullscreen = fullscreenElement && playerWrapper && (
        playerWrapper.contains(fullscreenElement) ||
        (fullscreenElement.contains(playerWrapper) &&
          fullscreenElement.querySelectorAll('.ezopen-player-wrapper').length === 1)
      );
      setIsFullscreen(!!isOurPlayerFullscreen);
    };

    handleFullscreenChange();

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Sync startTime and endTime props to internal state
  useEffect(() => {
    if (startTime) {
      setPlaybackStart(toPlaybackWallTime(startTime));
    }
    if (endTime) {
      setPlaybackEnd(toPlaybackWallTime(endTime));
    }
    // Event playback defaults to the recorder, with an explicit source override.
    if (startTime && endTime) {
      setStorageType(initialStorageType ?? 2);
    }
  }, [startTime, endTime, initialStorageType]);

  // Fetch cloud tokens for camera
  useEffect(() => {
    if (!camera.cloud_provider) {
      console.warn('⚠️ Camera has no cloud provider configured:', camera.id);
    }
  }, [camera]);

  useEffect(() => {
    let mounted = true;

    const fetchCloudTokens = async () => {
      try {
        const { cameraService } = await import('../../services');
        const data = await cameraService.getCloudTokens(camera.id);
        
        if (mounted) {
          setCloudTokens(data);
        }
      } catch (err: any) {
        console.error('❌ Failed to fetch cloud tokens:', err);
        if (mounted) {
          setError(err.message || 'Failed to authenticate with cloud provider');
          setLoading(false);
          callbacksRef.current.onError?.(err);
        }
      }
    };

    setCloudTokens(null);
    setLoading(true);
    setError(null);
    setIsPlaying(false);
    setIsPaused(false);
    if (camera.cloud_provider) {
      fetchCloudTokens();
    } else {
      // No cloud provider configured
      setError('Camera is not configured for cloud streaming');
      setLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [camera.id, camera.cloud_provider, connectionAttempt]);

  // Initialize EZOPEN player
  useEffect(() => {
    // Wait for all prerequisites
    if (!scriptsLoaded || !window.JSPlugin || !cloudTokens || !playerRef.current) {
      console.debug('⏳ Prerequisites check:', {
        scriptsLoaded,
        hasJSPlugin: !!window.JSPlugin,
        hasTimeBar: !!window.TimeBar,
        hasCloudTokens: !!cloudTokens,
        hasPlayerRef: !!playerRef.current,
        cameraId: camera.id
      });
      return;
    }

    let mounted = true;
    const initGeneration = ++lifecycleGenerationRef.current;
    let ownedPlugin: any = null;
    let resizeTimer: NodeJS.Timeout | null = null;
    let handleFullscreenResize: (() => void) | null = null;
    const isActive = () => mounted && isCurrentGeneration(initGeneration);

    const initializePlayer = async () => {
      try {
        if (!mounted) return;
        
        // A previous instance may still be stopping after React removed its tile.
        setLifecycleState('initializing');
        await waitForEZOPENCleanup();
        if (!isActive()) return;

        logPlayerEvent('Initialization started', { generation: initGeneration });
        setLifecycleState('initializing');
        setLoading(true);

        // Ensure player div has dimensions before creating plugin (prevents canvas errors)
        const playerDiv = playerRef.current;
        if (!playerDiv) {
          console.error('❌ Player div not found');
          return;
        }

        // Wait for DOM to be ready, especially in grid view with multiple players
        const delayMs = showControls ? 100 : 200; // Longer delay for grid view
        await waitForDelay(delayMs);
        if (!isActive()) return;

        // Check dimensions and wait if not ready
        let rect = playerDiv.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
          await waitForDelay(300);
          if (!isActive()) return;
          rect = playerDiv.getBoundingClientRect();
        }

        // Use actual container dimensions (parent-wnd will dictate size)
        const actualWidth = Math.floor(rect.width) || width;
        const actualHeight = Math.floor(rect.height) || height;
        
        console.log('🎥 Initializing player with dimensions:', { actualWidth, actualHeight, containerRect: rect });

        // Verify the target DOM element exists before creating JSPlugin
        const targetElement = document.getElementById(playerIdRef.current);
        if (!targetElement) {
          console.error('❌ Target element not found:', playerIdRef.current);
          throw new Error('Player container element not found');
        }

        const baseUrl = `${window.location.protocol}//${window.location.host}/ezopen`;
        const createPlugin = () => new window.JSPlugin({
          szId: playerIdRef.current,
          iWidth: actualWidth,
          iHeight: actualHeight,
          iMaxSplit: 1,
          iCurrentSplit: 1,
          szBasePath: baseUrl,
          bSupportSound: shouldEnableAudio,
          openDebug: false,
          openLogInfo: false,
          oStyle: { border: '#343434', borderSelect: '#00a0e9', background: '#000000' }
        });
        try {
          ownedPlugin = createPlugin();
        } catch (err: any) {
          if (!/width|canvas|null/.test(err?.message || '')) throw err;
          await waitForDelay(500);
          if (!isActive()) return;
          ownedPlugin = createPlugin();
        }
        pluginRef.current = ownedPlugin;

        // cbError is not a constructor option in JSPlugin 3.0. The supported
        // callback receives (windowIndex, eventCode, details).
        await ownedPlugin.JS_SetWindowControlCallback({
          onPluginEventHandler: (windowIndex: number, eventCode: number, details: any) => {
            if (!isActive() || pluginRef.current !== ownedPlugin) return;
            // Network/stream errors from the bundled SDK. 1006 is a video
            // coding error here, rather than the WebSocket close status.
            if ([1001, 1003, 1007, 1008, 1011].includes(eventCode)) {
              handleWebSocketError.current(1006);
            } else if (eventCode === 1002 && lastPlayParamsRef.current?.mode === 'playback') {
              stopProgressTimer.current();
              setIsPlaying(false);
              setLifecycleState('idle');
              updatePlaybackProgress(100);
            } else {
              logPlayerEvent('SDK event', { windowIndex, eventCode, details });
            }
          }
        });
        if (!isActive()) return;

        await waitForDelay(300);
        if (!isActive()) return;

        lastDimensionsRef.current = { width: actualWidth, height: actualHeight };
        const scheduleResize = (force = false) => {
          if (!isActive()) return;
          // Measure our host, rather than the SDK's wrapper, whose pixel size
          // may still reflect the previous fullscreen state.
          const rect = playerDiv.getBoundingClientRect();
          const next = { width: Math.floor(rect.width), height: Math.floor(rect.height) };
          if (next.width <= 0 || next.height <= 0 ||
              (!force && next.width === lastDimensionsRef.current?.width && next.height === lastDimensionsRef.current?.height)) return;
          lastDimensionsRef.current = next;
          if (resizeTimer) {
            clearTimeout(resizeTimer);
            activeTimersRef.current.delete(resizeTimer);
          }
          resizeTimer = createTrackedTimer(() => {
            if (!isActive()) return;
            void Promise.resolve(ownedPlugin.JS_Resize(next.width, next.height)).catch(() => {});
          }, 150);
        };
        resizeObserverRef.current = new ResizeObserver(() => scheduleResize());
        resizeObserverRef.current.observe(playerDiv);
        handleFullscreenResize = () => scheduleResize(true);
        document.addEventListener('fullscreenchange', handleFullscreenResize);
        document.addEventListener('webkitfullscreenchange', handleFullscreenResize);
        document.addEventListener('mozfullscreenchange', handleFullscreenResize);
        document.addEventListener('MSFullscreenChange', handleFullscreenResize);
        // Fullscreen may have opened while the SDK was initializing.
        scheduleResize();

        // Auto-play only in live mode with optional delay to stagger multiple players
        if (currentMode === 'live' && mounted) {
          const startAutoPlay = async () => {
            // Verify generation still valid
            if (!isCurrentGeneration(initGeneration)) {
              logPlayerEvent('Auto-play cancelled - generation changed');
              return;
            }
            
            try {
              logPlayerEvent('Auto-play starting');
              lastPlayParamsRef.current = { mode: 'live' };
              await playLiveStream();
              
              // Verify generation still valid after async operation
              if (!isCurrentGeneration(initGeneration)) {
                logPlayerEvent('Auto-play succeeded but generation changed - stopping');
                return;
              }
              
              setIsPlaying(true);
              setLifecycleState('playing');
              setLoading(false); // Clear loading on success
              logPlayerEvent('Auto-play successful');
            } catch (playError) {
              if (!isActive()) return;
              logPlayerEvent('Auto-play failed', { error: playError });
              setLoading(false); // Clear loading on failure so error is visible
              
              setError('Failed to start live stream - reconnecting...');
              setLifecycleState('error');
              void attemptReconnect.current();
            }
          };

          if (autoPlayDelay > 0) {
            createTrackedTimer(startAutoPlay, autoPlayDelay);
          } else {
            startAutoPlay();
          }
        }
        
        // In playback mode, auto-play if startTime and endTime are provided as props
        if (currentMode === 'playback' && startTime && endTime) {
          logPlayerEvent('Auto-playing playback with provided times');
          try {
            lastPlayParamsRef.current = { mode: 'playback', params: { startTime: playbackStart, endTime: playbackEnd, storageType } };
            const selection = await playRecording();
            
            // Verify generation still valid after async operation
            if (!isCurrentGeneration(initGeneration)) {
              logPlayerEvent('Playback auto-play succeeded but generation changed - stopping');
              return;
            }
            
            // Set playing state and start progress timer for timeline
            setIsPlaying(true);
            setLifecycleState('playing');
            updatePlaybackProgress(0);
            setLoading(false); // Clear loading on playback success
            startProgressTimer.current(selection.start, selection.end);
            logPlayerEvent('Playback auto-play successful - timeline started');
          } catch (err) {
            if (!isActive()) return;
            logPlayerEvent('Failed to auto-play recording', { error: err });
            setError(playbackErrorMessage(err));
            setLifecycleState('error');
            setLoading(false); // Clear loading on playback failure
          }
        }
        // Otherwise wait for user to click Play button

        if (isActive()) {
          setLoading(false);
          callbacksRef.current.onReady?.();
        }
      } catch (err) {
        if (!isActive()) return;
        console.error('❌ Failed to initialize player:', err);
        console.error('Error details:', {
          errorType: err instanceof Error ? err.constructor.name : typeof err,
          errorMessage: err instanceof Error ? err.message : String(err),
          errorStack: err instanceof Error ? err.stack : undefined,
          cloudTokens: cloudTokens ? 'present' : 'missing',
          scriptsLoaded,
          hasJSPlugin: !!window.JSPlugin,
          hasPlayerRef: !!playerRef.current
        });
        if (mounted) {
          const errorMsg = err instanceof Error ? err.message : 'Unknown error';
          setError(`Failed to initialize video player: ${errorMsg}`);
          setLoading(false);
          callbacksRef.current.onError?.(err);
        }
      }
    };

    initializePlayer().catch(err => {
      console.error('Unhandled error in initializePlayer:', err);
    });

    // Audio enable handler - listen to camera audio
    const handleAudioEnable = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { cameraId: eventCameraId, volume } = customEvent.detail;
      
      if (eventCameraId !== camera.id || !shouldEnableAudio || !isActive()) return;
      
      if (!pluginRef.current) {
        window.dispatchEvent(new CustomEvent('ezopen-audio-failed', {
          detail: { cameraId: camera.id, error: 'Player not ready' }
        }));
        return;
      }

      // Wait for playback to start
      const playbackIsActive = () => lifecycleStateRef.current === 'playing';
      if (!playbackIsActive()) {
        console.log('⏳ Waiting for playback...');
        let attempts = 0;
        while (isActive() && !playbackIsActive() && attempts < 30) {
          await waitForDelay(100);
          attempts++;
        }
      }

      if (!isActive() || !playbackIsActive()) return;
      const audioPlugin = ownedPlugin;
      try {
        // Open audio channel first time
        if (!audioOpenedRef.current) {
          await audioPlugin.JS_OpenSound(0);
          if (!isActive()) return;
          audioOpenedRef.current = true;
          console.log('🔊 Audio channel opened');
        }
        
        // Set volume
        await audioPlugin.JS_SetVolume(0, volume);
        if (!isActive()) return;
        console.log(`🔊 Audio volume set to ${volume}`);
        
        window.dispatchEvent(new CustomEvent('ezopen-audio-success', {
          detail: { cameraId: camera.id }
        }));
      } catch (err) {
        if (!isActive()) return;
        console.error('❌ Audio enable failed:', err);
        window.dispatchEvent(new CustomEvent('ezopen-audio-failed', {
          detail: { cameraId: camera.id, error: 'Failed to enable audio' }
        }));
      }
    };

    // Audio disable handler - stop camera audio
    const handleAudioDisable = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { cameraId: eventCameraId } = customEvent.detail;
      
      if (eventCameraId !== camera.id || !shouldEnableAudio || !isActive()) return;
      
      if (!pluginRef.current) return;

      try {
        await pluginRef.current.JS_SetVolume(0, 0);
        console.log('🔇 Audio stopped');
      } catch (err) {
        console.warn('⚠️ Audio stop failed:', err);
      }
    };

    window.addEventListener('ezopen-audio-enable', handleAudioEnable);
    window.addEventListener('ezopen-audio-disable', handleAudioDisable);

    // Cleanup on unmount
    return () => {
      mounted = false;
      
      // Increment lifecycle generation to invalidate ALL stale async operations
      lifecycleGenerationRef.current += 1;
      const cleanupGeneration = lifecycleGenerationRef.current;
      logPlayerEvent(`Cleanup started - invalidating generation ${cleanupGeneration - 1}`);
      
      // Mark as destroyed to prevent new operations
      lifecycleStateRef.current = 'destroyed';
      reconnectingRef.current = false;
      livePlayOperationRef.current = null;
      
      // Cancel ALL tracked timers immediately
      cancelAllTimers();
      
      // Disconnect resize observer
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (handleFullscreenResize) {
        document.removeEventListener('fullscreenchange', handleFullscreenResize);
        document.removeEventListener('webkitfullscreenchange', handleFullscreenResize);
        document.removeEventListener('mozfullscreenchange', handleFullscreenResize);
        document.removeEventListener('MSFullscreenChange', handleFullscreenResize);
      }
      
      // Remove audio event listeners
      window.removeEventListener('ezopen-audio-enable', handleAudioEnable);
      window.removeEventListener('ezopen-audio-disable', handleAudioDisable);
      
      // Stop playback progress polling
      stopProgressTimer.current();
      
      // Reset audio state
      audioOpenedRef.current = false;
      
      if (pluginRef.current === ownedPlugin) pluginRef.current = null;
      if (ownedPlugin) void destroyEZOPENPlugin(ownedPlugin);

      logPlayerEvent('Cleanup complete');
    };
    // Playback controls operate on this instance; changing picker values or
    // callbacks must not recreate its decoder workers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptsLoaded, cloudTokens, width, height, startTime, endTime, fillContainer, streamQuality, shouldEnableAudio, showControls, autoPlayDelay]);

  const playLiveStream = async () => {
    const generation = lifecycleGenerationRef.current;
    const existing = livePlayOperationRef.current;
    if (existing?.generation === generation) return existing.promise;

    const plugin = pluginRef.current;
    if (!plugin || !cloudTokens) throw new Error('Player or cloud tokens not ready');
    const assertActive = () => {
      if (!isCurrentGeneration(generation) || pluginRef.current !== plugin) {
        throw new Error('Player operation cancelled');
      }
    };
    const operation = (async () => {
      assertActive();
      audioOpenedRef.current = false;
      const { stream_domain, serial_no, channel_no, app_token, verification_code } = cloudTokens;
      const quality = streamQuality === 'hd' ? '.hd' : '';
      const url = `ezopen://open.ezviz.com/${serial_no}/${channel_no}${quality}.live`;
      const playOptions = {
        playURL: url,
        ezuikit: true,
        env: { domain: stream_domain },
        accessToken: app_token,
        mode: 'media',
        sound: shouldEnableAudio ? 1 : 0
      };
      try {
        await plugin.JS_Stop(0);
      } catch (error) {
        // A fresh player may not have a stream to stop.
      }
      assertActive();
      if (verification_code) {
        await plugin.JS_SetSecretKey(0, verification_code);
        assertActive();
      }
      try {
        await plugin.JS_Play(url, playOptions, 0);
      } catch (error: any) {
        assertActive();
        const message = error?.oError?.msg || error?.message || String(error);
        if (!/forceStoped|forceStopped/.test(message)) throw error;
        await plugin.JS_Stop(0);
        assertActive();
        await waitForDelay(300);
        assertActive();
        await plugin.JS_Play(url, playOptions, 0);
      }
      assertActive();
      reconnectAttemptsRef.current = 0;
    })();
    livePlayOperationRef.current = { generation, promise: operation };
    try {
      await operation;
    } finally {
      if (livePlayOperationRef.current?.promise === operation) livePlayOperationRef.current = null;
    }
  };

  const playRecording = async () => {
    const plugin = pluginRef.current;
    const generation = lifecycleGenerationRef.current;
    const assertActive = () => {
      if (!isCurrentGeneration(generation) || pluginRef.current !== plugin) throw new Error('Player operation cancelled');
    };
    if (!plugin || !cloudTokens) {
      throw new Error('Player or cloud tokens not ready for playback');
    }

    // Verify plugin has required methods
    if (typeof plugin.JS_Play !== 'function') {
      console.error('❌ JSPlugin not fully initialized - missing required methods');
      throw new Error('Player not ready for playback');
    }

    assertActive();

    // Reset audio state for new playback
    audioOpenedRef.current = false;

    const { stream_domain, serial_no, channel_no, app_token, verification_code } = cloudTokens;
    
    // Read the latest selection after SDK initialization, including prop changes
    // synchronized by an effect while the new player was becoming ready.
    const { start: startTimeStr, end: endTimeStr, storageType: selectedStorage } = playbackSelectionRef.current;
    
    if (!startTimeStr || !endTimeStr) {
      throw new Error('Start and end time are required for playback');
    }

    if (!(playbackMillis(endTimeStr) > playbackMillis(startTimeStr))) {
      throw new Error('Choose an end time after the start time.');
    }

    // Ensure clean state - stop any existing stream first
    try {
      if (typeof plugin.JS_Stop === 'function') {
        await plugin.JS_Stop(0);
        await waitForDelay(100);
      }
    } catch (stopErr) {
      // Ignore stop errors - stream might not be playing
    }

    assertActive();
    try {
      // Set the verification code before opening the recording.
      if (verification_code) {
        try {
          await plugin.JS_SetSecretKey(0, verification_code);
          assertActive();
        } catch (err) {
          console.error('❌ Failed to set verification code:', err);
          throw new Error('Failed to set verification code');
        }
      } else {
        console.warn('⚠️ No verification code provided');
      }

      // JS_Play performs the provider lookup itself. A separate VideoSearch
      // used a different timezone and could reject a valid recorder range;
      // seeking worked because it already went directly through JS_Play.
      const tokenOptions = { timeZone: playbackTimezone, wallAnchor: startTime, utcAnchor: searchStartTime };
      const formattedStart = recordingToken(startTimeStr, selectedStorage, tokenOptions);
      const formattedEnd = recordingToken(endTimeStr, selectedStorage, {
        timeZone: playbackTimezone, wallAnchor: endTime, utcAnchor: searchEndTime });

      // EZOPEN playback URL: ezopen://open.ezviz.com/serial/channel.storageType.rec?begin=time&end=time
      const storageTypeStr = selectedStorage === 1 ? 'cloud' : 'local';
      const url = `ezopen://open.ezviz.com/${serial_no}/${channel_no}.${storageTypeStr}.rec?begin=${formattedStart}&end=${formattedEnd}`;
      // Play recording
      try {
        await plugin.JS_Play(
          url,
          {
            playURL: url,
            ezuikit: true,
            env: {
              domain: stream_domain
            },
            accessToken: app_token,
            mode: 'media',
            sound: shouldEnableAudio ? 1 : 0
          },
          0, // Window index
          formattedStart,
          formattedEnd
        );
        assertActive();
        reconnectAttemptsRef.current = 0;
        lastPlayParamsRef.current = { mode:'playback', params:{ startTime:startTimeStr, endTime:endTimeStr, storageType:selectedStorage } };
        return { start: startTimeStr, end: endTimeStr, storageType: selectedStorage };
      } catch (playErr: any) {
        assertActive();
        console.error('❌ JS_Play failed:', playErr);
        console.error('Play error details:', {
          type: typeof playErr,
          value: playErr,
          errorCode: playErr?.iErrorNum || playErr?.oError?.code,
          errorMessage: playErr?.oError?.msg || playErr?.message
        });
        
        // Extract error code to throw
        const errorCode = playErr?.iErrorNum || playErr?.oError?.code;
        throw errorCode || playErr;
      }
    } catch (err: any) {
      console.error('❌ Failed to play recording:', err);
      
      // Extract error code from EZOPEN error structure
      const errorCode = typeof err === 'number' ? err : (err?.iErrorNum || err?.oError?.code || err?.code);
      const errorMessage = err?.oError?.msg || err?.message;
      
      console.error('Playback error details:', {
        error: err,
        errorCode: errorCode,
        errorMessage: errorMessage,
        cloudTokens: {
          serial: serial_no,
          channel: channel_no,
          domain: stream_domain,
          hasToken: !!app_token,
          hasVerificationCode: !!verification_code
        },
        timeRange: {
          start: startTimeStr,
          end: endTimeStr,
          storageType: selectedStorage === 1 ? 'cloud' : 'local'
        }
      });
      
      // Throw error code if available, otherwise throw the whole error
      if (errorCode) {
        throw errorCode;
      } else {
        throw err;
      }
    }
  };

  const handleModeSwitch = async (newMode: 'live' | 'playback') => {
    if (!pluginRef.current) return;
    
    try {
      // Stop playback progress polling
      stopProgressTimer.current();
      
      // Clear reconnect timer
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      
      // Stop recording if active
      if (isRecording) {
        try {
          await pluginRef.current.JS_StopSave?.(0);
          setIsRecording(false);
        } catch (err) {
          console.warn('Failed to stop recording during mode switch:', err);
        }
      }
      
      // Stop current stream
      try {
        await pluginRef.current.JS_Stop(0);
      } catch (stopErr: any) {
        // "forceStoped" is a normal state when stopping, not an error
        const stopMsg = stopErr?.oError?.msg || stopErr?.message || String(stopErr);
        if (!stopMsg.includes('forceStoped') && !stopMsg.includes('forceStopped')) {
          console.warn('Warning while stopping stream:', stopMsg);
        }
      }
      
      // Wait for stream to fully stop before starting new one
      await waitForDelay(300);
      
      setCurrentMode(newMode);
      setLifecycleState('idle');
      setIsPlaying(false);
      setError(null);
      
      // Play new mode
      if (newMode === 'live') {
        lastPlayParamsRef.current = { mode: 'live' };
        await playLiveStream();
        setIsPlaying(true);
        setLifecycleState('playing');
      }
    } catch (err: any) {
      console.error('Failed to switch mode:', err);
      
      // Filter out "forceStoped" errors which are normal during stream switching
      const errorMsg = err?.oError?.msg || err?.message || String(err) || 'Unknown error';
      if (errorMsg.includes('forceStoped') || errorMsg.includes('forceStopped')) {
        // Retry playing the stream
        try {
          if (newMode === 'live') {
            await waitForDelay(200);
            await playLiveStream();
            setIsPlaying(true);
            return;
          }
        } catch (retryErr) {
          console.error('Retry failed:', retryErr);
        }
      }
      
      setError(`Failed to switch to ${newMode} mode: ${errorMsg}`);
      setIsPlaying(false);
      stopProgressTimer.current();
    }
  };

  const handlePlayback = async () => {
    const generation = lifecycleGenerationRef.current;
    if (!pluginRef.current || !cloudTokens) {
      console.error('Cannot start playback: plugin or tokens not ready');
      return;
    }
    
    try {
      stopProgressTimer.current();
      setLoading(true);
      setError(null);
      updatePlaybackProgress(0); // Reset timeline progress
      
      // Clear reconnect timer
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      
      // Save play parameters for reconnection
      lastPlayParamsRef.current = { 
        mode: 'playback',
        params: { startTime: playbackStart, endTime: playbackEnd, storageType }
      };
      
      const selection = await playRecording();
      if (!isCurrentGeneration(generation)) return;
      setIsPaused(false);
      setIsPlaying(true);
      setLifecycleState('playing');
      setLoading(false);
      
      // Start progress timer for timeline
      startProgressTimer.current(selection.start, selection.end);
    } catch (err: any) {
      if (!isCurrentGeneration(generation)) return;
      console.error('Failed to start playback:', err);
      setError(playbackErrorMessage(err));
      setLoading(false);
      setIsPlaying(false);
      stopProgressTimer.current();
    }
  };

  const handleStop = async () => {
    if (!pluginRef.current) return;
    
    try {
      // Stop playback progress polling
      stopProgressTimer.current();
      
      // Clear reconnect timer
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      
      // Stop recording if active
      if (isRecording) {
        await handleStopRecording();
      }
      
      await pluginRef.current.JS_Stop(0);
      setLifecycleState('idle');
      setIsPlaying(false);
      setIsPaused(false);
      updatePlaybackProgress(0); // Reset timeline progress
    } catch (err) {
      console.error('Failed to stop:', err);
    }
  };

  const handlePause = async () => {
    if (!pluginRef.current || currentMode !== 'playback') return;
    const generation = lifecycleGenerationRef.current;
    
    try {
      await pluginRef.current.JS_Pause(0);
      if (!isCurrentGeneration(generation)) return;
      setIsPaused(true);
      stopProgressTimer.current();
    } catch (err) {
      console.error('Failed to pause:', err);
    }
  };

  const handleResume = async () => {
    if (!pluginRef.current || currentMode !== 'playback') return;
    const generation = lifecycleGenerationRef.current;
    
    try {
      await pluginRef.current.JS_Resume(0);
      if (!isCurrentGeneration(generation)) return;
      setIsPaused(false);
      startProgressTimer.current(playbackStart, playbackEnd);
    } catch (err) {
      console.error('Failed to resume:', err);
    }
  };

  const handleStartRecording = async () => {
    if (!pluginRef.current || !isPlaying) return;
    
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `recording_${camera.id}_${timestamp}.mp4`;
      await pluginRef.current.JS_StartSave?.(0, filename);
      setIsRecording(true);
    } catch (err) {
      console.error('Failed to start recording:', err);
    }
  };

  // Seek within the selected recording; retain seconds and recorder wall time.
  const handleSeek = async (percentage: number) => {
    const plugin = pluginRef.current;
    const generation = lifecycleGenerationRef.current;
    const isActive = () => plugin && pluginRef.current === plugin && isCurrentGeneration(generation);
    if (!isActive() || currentMode !== 'playback' || !cloudTokens) return;
    stopProgressTimer.current();
    setLoading(true); setError(null);
    const position = Math.min(99.9, Math.max(0, percentage));
    try {
      const start = playbackMillis(playbackStart), end = playbackMillis(playbackEnd);
      const target = playbackWallTimeAt(start + (end - start) * position / 100);
      const { stream_domain, serial_no, channel_no, app_token, verification_code } = cloudTokens;
      try { await plugin.JS_Stop(0); } catch { /* No active recording. */ }
      if (!isActive()) return;
      if (verification_code) await plugin.JS_SetSecretKey(0, verification_code);
      if (!isActive()) return;
      const tokenOptions = { timeZone: playbackTimezone, wallAnchor: startTime, utcAnchor: searchStartTime };
      const begin = recordingToken(target, storageType, tokenOptions), finish = recordingToken(playbackEnd, storageType, {
        timeZone: playbackTimezone, wallAnchor: endTime, utcAnchor: searchEndTime });
      const source = storageType === 1 ? 'cloud' : 'local';
      const url = `ezopen://open.ezviz.com/${serial_no}/${channel_no}.${source}.rec?begin=${begin}&end=${finish}`;
      await plugin.JS_Play(url, { playURL: url, ezuikit: true, env: { domain: stream_domain }, accessToken: app_token,
        mode: 'media', sound: shouldEnableAudio ? 1 : 0 }, 0, begin, finish);
      if (!isActive()) return;
      audioOpenedRef.current = false;
      updatePlaybackProgress(position); setIsPaused(false); setIsPlaying(true); setLifecycleState('playing');
      startProgressTimer.current(playbackStart, playbackEnd);
    } catch (err) {
      if (!isActive()) return;
      setError(playbackErrorMessage(err)); setIsPlaying(false); setIsPaused(false);
    } finally { if (isActive()) setLoading(false); }
  };

  const handleStopRecording = async () => {
    if (!pluginRef.current || !isRecording) return;
    
    try {
      await pluginRef.current.JS_StopSave?.(0);
      setIsRecording(false);
    } catch (err) {
      console.error('Failed to stop recording:', err);
    }
  };

  const useOverlayControls = showControls && (isFullscreen || fillContainer);
  const intercomControl = showIntercom && currentMode === 'live' ? <CameraIntercomControl
    cameraId={camera.id} cameraName={cameraName} supported={cameraIntercomSupport(camera)} size={audioControlsSize} onConnectionChange={setIntercomInUse} /> : null;
  const handleControlPlay = async () => {
    if (currentMode === 'playback') return handlePlayback();
    const generation = lifecycleGenerationRef.current;
    setLoading(true);
    setError(null);
    try {
      lastPlayParamsRef.current = { mode: 'live' };
      await playLiveStream();
      if (!isCurrentGeneration(generation)) return;
      setIsPlaying(true);
      setIsPaused(false);
      setLifecycleState('playing');
    } catch (err: any) {
      if (!isCurrentGeneration(generation)) return;
      setError(err?.message || 'Failed to start playback');
      setIsPlaying(false);
    } finally {
      if (isCurrentGeneration(generation)) setLoading(false);
    }
  };
  const handleControlFullscreen = () => {
    if (onToggleFullscreen) return onToggleFullscreen();
    const wrapper = playerRef.current?.closest('.ezopen-player-wrapper') as HTMLElement | null;
    const operation = document.fullscreenElement ? document.exitFullscreen() : wrapper?.requestFullscreen?.();
    void operation?.catch(err => console.error('Failed to change player fullscreen:', err));
  };
  const retryConnection = () => {
    reconnectAttemptsRef.current = 0;
    setLoading(true);
    setError(null);
    setConnectionAttempt(attempt => attempt + 1);
  };

  return (
    <div className="ezopen-player-wrapper" style={{ 
      position: 'relative', 
      width: '100%',
      height: '100%',
      maxWidth: (fillContainer || isFullscreen) ? 'none' : (showControls ? '1200px' : 'none'),
      margin: '0 auto',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      {/* Keep sizing rules outside the host: JSPlugin replaces its innerHTML. */}
      <style>{`
        #${playerIdRef.current},
        #${playerIdRef.current} > .parent-wnd,
        #${playerIdRef.current} > .parent-wnd > div[id*="player-container-"],
        #${playerIdRef.current} .video-container {
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
        }
        #${playerIdRef.current} > .parent-wnd {
          position: relative !important;
        }
        #${playerIdRef.current} > .parent-wnd > div[id*="player-container-"],
        #${playerIdRef.current} .video-container {
          position: absolute !important;
          inset: 0 !important;
          border: none !important;
          float: none !important;
        }
        #${playerIdRef.current} canvas,
        #${playerIdRef.current} video {
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
          object-fit: ${videoFit} !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
        }
      `}</style>

      {/* Player container with modern styling */}
      <div 
        className="ezopen-player-container"
        onMouseMove={(isFullscreen || fillContainer) ? resetInactivityTimer : undefined}
        onMouseEnter={(isFullscreen || fillContainer) ? resetInactivityTimer : undefined}
        style={{ 
          position: 'relative', 
          width: '100%', 
          height: '100%',
          minHeight: showControls ? 'auto' : '100%',
          borderRadius: (isFullscreen || fillContainer) ? '0' : (showControls ? '12px' : '0'),
          overflow: 'hidden',
          boxShadow: (isFullscreen || fillContainer) || !showControls ? 'none' : '0 4px 20px rgba(0, 0, 0, 0.3)',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: (isFullscreen || fillContainer) && !showFullscreenControls ? 'none' : 'default'
        }}>
        {/* Loading overlay with spinner */}
        {(loading || !scriptsLoaded || !cloudTokens) && !error && (
          <div 
            className="flex items-center justify-center w-full h-full"
            style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              backgroundColor: 'rgba(0, 0, 0, 0.95)', 
              zIndex: 10,
              backdropFilter: 'blur(10px)'
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid rgba(59, 130, 246, 0.3)',
                borderTop: '4px solid #3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }} />
              <div style={{ color: '#fff', fontSize: '14px', fontWeight: 500 }}>
                {!scriptsLoaded ? 'Initializing player...' : 
                 !cloudTokens ? 'Authenticating...' : 
                 'Loading stream...'}
              </div>
              <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '8px' }}>
                {!scriptsLoaded ? 'Loading video engine' : 
                 !cloudTokens ? 'Connecting to cloud service' : 
                 'Please wait'}
              </div>
            </div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              
              /* Custom scrollbar for controls panel */
              .ezopen-controls-panel::-webkit-scrollbar {
                width: 8px;
              }
              
              .ezopen-controls-panel::-webkit-scrollbar-track {
                background: rgba(15, 23, 42, 0.5);
                border-radius: 4px;
              }
              
              .ezopen-controls-panel::-webkit-scrollbar-thumb {
                background: linear-gradient(180deg, #3b82f6, #2563eb);
                border-radius: 4px;
              }
              
              .ezopen-controls-panel::-webkit-scrollbar-thumb:hover {
                background: linear-gradient(180deg, #60a5fa, #3b82f6);
              }
            `}</style>
          </div>
        )}
        
        {error && <div className="ezopen-playback-error" role="alert">
          <div><span className="ezopen-playback-error__eyebrow">{currentMode === 'playback' ? 'Camera recording' : 'Camera connection'}</span>
            <h3>{!cloudTokens ? 'Connection unavailable' : currentMode === 'playback' ? 'Recording unavailable' : 'Stream unavailable'}</h3>
            <p>{error}</p>
            {eventTime && <small>Use the source and time window above to find another recording, or reconnect to try again.</small>}
            <button type="button" className="ezopen-playback-error__retry" onClick={event => {
              event.stopPropagation();
              retryConnection();
            }}>Retry connection</button>
          </div>
        </div>}

        {/* Compact status for grid tiles and embedded players. */}
        {!useOverlayControls && <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          zIndex: 5,
          display: 'flex',
          gap: '8px',
          opacity: (isFullscreen || fillContainer) ? (showFullscreenControls ? 1 : 0) : 1,
          transition: 'opacity 0.3s ease',
          pointerEvents: (isFullscreen || fillContainer) && !showFullscreenControls ? 'none' : 'auto'
        }}>
          {isRecording && (
            <div style={{
              backgroundColor: 'rgba(239, 68, 68, 0.9)',
              color: '#fff',
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backdropFilter: 'blur(10px)',
              animation: 'pulse 2s ease-in-out infinite'
            }}>
              <span style={{ 
                width: '8px', 
                height: '8px', 
                backgroundColor: '#fff',
                borderRadius: '50%',
                display: 'inline-block'
              }} />
              REC
            </div>
          )}
          {showControls && isPlaying && !isPaused && (
            <div style={{
              backgroundColor: 'rgba(34, 197, 94, 0.9)',
              color: '#fff',
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 600,
              backdropFilter: 'blur(10px)'
            }}>
              {currentMode === 'live' ? '🔴 LIVE' : '▶ PLAYING'}
            </div>
          )}
          {showControls && isPaused && (
            <div style={{
              backgroundColor: 'rgba(251, 191, 36, 0.9)',
              color: '#fff',
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 600,
              backdropFilter: 'blur(10px)'
            }}>
              ⏸ PAUSED
            </div>
          )}
        </div>}

        {/* Audio Controls */}
        {((showAudioControls && audioControls.isAudioSupported) || intercomControl) && !useOverlayControls && (
          <div style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            zIndex: 5,
            display: 'flex', alignItems: 'center', gap: '4px',
            opacity: (isFullscreen || fillContainer) ? (showFullscreenControls || intercomInUse ? 1 : 0) : 1,
            transition: 'opacity 0.3s ease',
            pointerEvents: (isFullscreen || fillContainer) && !showFullscreenControls && !intercomInUse ? 'none' : 'auto'
          }}>
            {showAudioControls && <AudioControls
              mode={audioControls.audioMode}
              isActive={audioControls.isAudioActive}
              isLoading={audioControls.isAudioLoading}
              error={audioControls.audioError}
              isSupported={audioControls.isAudioSupported}
              size={audioControlsSize}
              showLabel={false}
              onCycleMode={audioControls.cycleAudioMode}
            />}
            {intercomControl}
          </div>
        )}
        
        {/* React owns the host; EZOPEN owns all of its child nodes. */}
        <div
          id={playerIdRef.current}
          ref={playerRef}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#000'
          }}
        />

        {useOverlayControls && (
          <div
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 20 }}
            onMouseEnter={() => { controlsHoveredRef.current = true; resetInactivityTimer(); }}
            onMouseLeave={() => { controlsHoveredRef.current = false; resetInactivityTimer(); }}
            onFocusCapture={() => { controlsFocusedRef.current = true; resetInactivityTimer(); }}
            onBlurCapture={event => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                controlsFocusedRef.current = false;
                resetInactivityTimer();
              }
            }}
          >
            <EZOPENControls
              cameraName={cameraName || `Camera ${camera.id}`}
              mode={currentMode}
              quality={streamQuality}
              playing={isPlaying}
              paused={isPaused}
              recording={isRecording}
              loading={loading}
              visible={showFullscreenControls || !isPlaying || isPaused || loading || intercomInUse}
              fullscreen={isFullscreen}
              autoPlayback={isAutoPlayMode}
              audioSupported={shouldEnableAudio && audioControls.isAudioSupported}
              audioMuted={audioControls.isMuted}
              audioLoading={audioControls.isAudioLoading}
              audioError={audioControls.audioError}
              intercomControl={intercomControl}
              storageType={storageType}
              startTime={playbackStart}
              endTime={playbackEnd}
              progress={playbackProgress}
              eventPosition={eventTime ? (playbackMillis(eventTime) - playbackMillis(playbackStart)) / (playbackMillis(playbackEnd) - playbackMillis(playbackStart)) * 100 : undefined}
              onPlay={handleControlPlay}
              onPause={handlePause}
              onResume={handleResume}
              onStop={handleStop}
              onModeChange={handleModeSwitch}
              onRecord={isRecording ? handleStopRecording : handleStartRecording}
              onToggleAudio={audioControls.cycleAudioMode}
              onToggleFullscreen={handleControlFullscreen}
              onStorageChange={setStorageType}
              onStartTimeChange={setPlaybackStart}
              onEndTimeChange={setPlaybackEnd}
              onSeek={handleSeek}
            />
          </div>
        )}
      </div>

      {/* Player Controls Panel - Shows below player in normal mode (not fullscreen or fillContainer) */}
      {showControls && !isFullscreen && !fillContainer && (
        <div 
          className="player-controls-panel"
          style={{
            background: 'linear-gradient(to bottom, #0f172a 0%, #1e293b 100%)',
            padding: '12px 16px',
            borderTop: '1px solid rgba(59, 130, 246, 0.3)',
            boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.2)'
          }}
        >
          {/* Playback Controls Row */}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '8px' }}>
            {/* Play/Pause/Stop */}
            {!isPlaying ? (
              <button
                onClick={currentMode === 'live' ? async () => {
                  try {
                    lastPlayParamsRef.current = { mode: 'live' };
                    await playLiveStream();
                    setIsPlaying(true);
                  } catch (err: any) {
                    console.error('❌ Failed to play stream:', err);
                    setError(err?.message || 'Failed to start playback');
                    setIsPlaying(false);
                  }
                } : handlePlayback}
                style={{
                  padding: '6px 12px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  boxShadow: '0 2px 6px rgba(16, 185, 129, 0.4)',
                  whiteSpace: 'nowrap'
                }}
              >
                ▶ Play
              </button>
            ) : (
              <>
                {currentMode === 'playback' && (
                  <button
                    onClick={isPaused ? handleResume : handlePause}
                    style={{
                      padding: '6px 12px',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 600,
                      boxShadow: '0 2px 6px rgba(245, 158, 11, 0.4)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {isPaused ? '▶' : '⏸'}
                  </button>
                )}
                <button
                  onClick={handleStop}
                  style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    boxShadow: '0 2px 6px rgba(239, 68, 68, 0.4)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  ⏹
                </button>
              </>
            )}
            
            {/* Recording Button */}
            {currentMode === 'playback' && isPlaying && (
              <button
                onClick={isRecording ? handleStopRecording : handleStartRecording}
                style={{
                  padding: '5px 10px',
                  background: isRecording ? 
                    'linear-gradient(135deg, #64748b 0%, #475569 100%)' : 
                    'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '10px',
                  fontWeight: 600,
                  boxShadow: `0 2px 6px rgba(${isRecording ? '100, 116, 139' : '239, 68, 68'}, 0.4)`,
                  whiteSpace: 'nowrap',
                  marginLeft: 'auto'
                }}
              >
                {isRecording ? '⏹ Rec' : '● Rec'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Settings Panel - Mode toggle and playback settings */}
      {showControls && !(isFullscreen || fillContainer) && (
        <div style={{ 
          background: 'linear-gradient(to bottom, #1f2937 0%, #111827 100%)',
          borderRadius: '0 0 12px 12px',
          padding: '12px 16px',
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.2)'
        }}>
          {/* Compact Controls Row - Mode + Settings (Hide in auto-play mode) */}
          {!isAutoPlayMode && (
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            alignItems: 'center',
            marginBottom: (isFullscreen || fillContainer) && currentMode === 'playback' ? '8px' : '20px'
          }}>
            {/* Mode Toggle - Compact */}
            <div style={{ 
              display: 'flex', 
              gap: '2px', 
              backgroundColor: 'rgba(15, 23, 42, 0.4)',
              borderRadius: '6px',
              padding: '3px'
            }}>
              <button
                onClick={() => handleModeSwitch('live')}
                disabled={currentMode === 'live' && isPlaying}
                style={{
                  padding: '6px 16px',
                  backgroundColor: currentMode === 'live' ? '#3b82f6' : 'transparent',
                  color: currentMode === 'live' ? '#fff' : '#94a3b8',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: currentMode === 'live' && isPlaying ? 'not-allowed' : 'pointer',
                  fontSize: '11px',
                  fontWeight: 600,
                  opacity: currentMode === 'live' && isPlaying ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                🔴 Live
              </button>
              <button
                onClick={() => handleModeSwitch('playback')}
                disabled={currentMode === 'playback'}
                style={{
                  padding: '6px 16px',
                  backgroundColor: currentMode === 'playback' ? '#3b82f6' : 'transparent',
                  color: currentMode === 'playback' ? '#fff' : '#94a3b8',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: currentMode === 'playback' ? 'not-allowed' : 'pointer',
                  fontSize: '11px',
                  fontWeight: 600,
                  opacity: currentMode === 'playback' ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                📹 Playback
              </button>
              {/* Recording Button */}
              {isPlaying && (
                <button
                  onClick={isRecording ? handleStopRecording : handleStartRecording}
                  style={{
                    padding: '6px 16px',
                    backgroundColor: isRecording ? '#64748b' : '#ef4444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {isRecording ? '⏹ Stop Rec' : '● Rec'}
                </button>
              )}
            </div>
            
            {/* Storage and Time Pickers - Only show in playback mode */}
            {currentMode === 'playback' && (
              <>
                {/* Storage Type */}
                <select
                  value={storageType}
                  onChange={(e) => setStorageType(Number(e.target.value) as 1 | 2)}
                  disabled={isPlaying}
                  style={{
                    padding: '6px 8px',
                    backgroundColor: '#1e293b',
                    color: '#e2e8f0',
                    border: '1px solid #334155',
                    borderRadius: '4px',
                    fontSize: '11px',
                    cursor: isPlaying ? 'not-allowed' : 'pointer',
                    outline: 'none'
                  }}
                >
                  <option value={1}>☁️ Cloud</option>
                  <option value={2}>💾 Local</option>
                </select>
                
                {/* Start Time */}
                <input
                  type="datetime-local" step={1}
                  value={playbackStart}
                  onChange={(e) => setPlaybackStart(e.target.value)}
                  disabled={isPlaying}
                  style={{
                    padding: '6px 8px',
                    backgroundColor: '#1e293b',
                    color: '#e2e8f0',
                    border: '1px solid #334155',
                    borderRadius: '4px',
                    fontSize: '11px',
                    cursor: isPlaying ? 'not-allowed' : 'pointer',
                    outline: 'none'
                  }}
                />
                
                {/* End Time */}
                <input
                  type="datetime-local" step={1}
                  value={playbackEnd}
                  onChange={(e) => setPlaybackEnd(e.target.value)}
                  disabled={isPlaying}
                  style={{
                    padding: '6px 8px',
                    backgroundColor: '#1e293b',
                    color: '#e2e8f0',
                    border: '1px solid #334155',
                    borderRadius: '4px',
                    fontSize: '11px',
                    cursor: isPlaying ? 'not-allowed' : 'pointer',
                    outline: 'none'
                  }}
                />
              </>
            )}
          </div>
          )}

          {/* Compact Timeline for seeking (Show in playback mode) */}
          {currentMode === 'playback' && playbackStart && playbackEnd && (!(isFullscreen || fillContainer) || showFullscreenControls) && (
            <div style={{ 
              marginBottom: '16px',
              backgroundColor: '#0f172a',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #1e293b',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
            }}>
              {/* Timeline label - hide in compact mode */}
              {!(isFullscreen || fillContainer) && (
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <div style={{ 
                    color: '#94a3b8', 
                    fontSize: '11px', 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px'
                  }}>
                    📹 Recording Timeline
                  </div>
                  {timelineHoverPercent !== null && (
                    <div style={{
                      color: '#60a5fa',
                      fontSize: '11px',
                      fontWeight: 600,
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      border: '1px solid rgba(59, 130, 246, 0.3)'
                    }}>
                      {(() => {
                        const start = new Date(playbackStart).getTime();
                        const end = new Date(playbackEnd).getTime();
                        const duration = end - start;
                        const hoverTime = new Date(start + (duration * timelineHoverPercent / 100));
                        return hoverTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                      })()}
                    </div>
                  )}
                </div>
              )}
              
              {/* Timeline Track */}
              <div 
                style={{
                  position: 'relative',
                  width: '100%',
                  height: (isFullscreen || fillContainer) ? '24px' : '48px',
                  backgroundColor: '#1e293b',
                  borderRadius: (isFullscreen || fillContainer) ? '4px' : '8px',
                  cursor: isPlaying ? 'pointer' : 'not-allowed',
                  overflow: 'hidden',
                  border: (isFullscreen || fillContainer) ? '1px solid #334155' : '2px solid #334155',
                  boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.5)'
                }}
                onClick={(e) => {
                  if (!isPlaying) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                  handleSeek(percentage);
                }}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                  setTimelineHoverPercent(percentage);
                }}
                onMouseLeave={() => setTimelineHoverPercent(null)}
              >
                {/* Background gradient */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)'
                }} />
                
                {/* Progress fill with gradient */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: `${playbackProgress}%`,
                  background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
                  transition: 'width 0.3s ease',
                  boxShadow: '0 0 20px rgba(59, 130, 246, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px 0 0 6px'
                }} />
                
                {/* Hover indicator */}
                {timelineHoverPercent !== null && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: `${timelineHoverPercent}%`,
                    bottom: 0,
                    width: '2px',
                    backgroundColor: '#60a5fa',
                    boxShadow: '0 0 10px rgba(96, 165, 250, 0.8)',
                    zIndex: 10
                  }} />
                )}
                
                {/* Progress handle */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: `${playbackProgress}%`,
                  transform: 'translate(-50%, -50%)',
                  width: (isFullscreen || fillContainer) ? '12px' : '16px',
                  height: (isFullscreen || fillContainer) ? '12px' : '16px',
                  backgroundColor: '#fff',
                  borderRadius: '50%',
                  border: (isFullscreen || fillContainer) ? '2px solid #3b82f6' : '3px solid #3b82f6',
                  boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.3), 0 2px 8px rgba(0, 0, 0, 0.5)',
                  transition: 'left 0.3s ease',
                  zIndex: 20
                }} />
                
                {/* Time markers - hide in compact mode */}
                {!(isFullscreen || fillContainer) && (
                  <>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '8px',
                      transform: 'translateY(-50%)',
                      fontSize: '9px',
                      fontWeight: 600,
                      color: '#64748b',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)',
                      pointerEvents: 'none',
                      zIndex: 5
                    }}>
                      {new Date(playbackStart).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      right: '8px',
                      transform: 'translateY(-50%)',
                      fontSize: '9px',
                      fontWeight: 600,
                      color: '#64748b',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)',
                      pointerEvents: 'none',
                      zIndex: 5
                    }}>
                      {new Date(playbackEnd).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </>
                )}
              </div>
              
              {/* Progress info - hide in compact mode */}
              {!(isFullscreen || fillContainer) && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '10px'
                }}>
                  <div style={{
                    fontSize: '10px',
                    color: '#64748b',
                    fontWeight: 500
                  }}>
                    {isPlaying ? (isPaused ? '⏸ Paused' : '▶ Playing') : '⏹ Stopped'}
                  </div>
                  <div style={{
                    fontSize: '10px',
                    color: '#94a3b8',
                    fontWeight: 600
                  }}>
                    {Math.round(playbackProgress)}% Complete
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Compact Settings - Only show in normal mode (not fullscreen) */}
          {!isAutoPlayMode && currentMode === 'playback' && !(isFullscreen || fillContainer) && (
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 1fr', gap: '10px', alignItems: 'end', marginBottom: '16px' }}>
              
              {/* Storage Type */}
              <div>
                <label style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>
                  Storage
                </label>
                <select
                  value={storageType}
                  onChange={(e) => setStorageType(Number(e.target.value) as 1 | 2)}
                  disabled={isPlaying}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    backgroundColor: '#1e293b',
                    color: '#e2e8f0',
                    border: '1px solid #334155',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: isPlaying ? 'not-allowed' : 'pointer',
                    outline: 'none'
                  }}
                >
                  <option value={1}>☁️ Cloud</option>
                  <option value={2}>💾 Local</option>
                </select>
              </div>
              
              {/* Start Time */}
              <div>
                <label style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>
                  Start Time
                </label>
                <input
                  type="datetime-local" step={1}
                  value={playbackStart}
                  onChange={(e) => setPlaybackStart(e.target.value)}
                  disabled={isPlaying}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    backgroundColor: '#1e293b',
                    color: '#e2e8f0',
                    border: '1px solid #334155',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: isPlaying ? 'not-allowed' : 'pointer',
                    outline: 'none'
                  }}
                />
              </div>
              
              {/* End Time */}
              <div>
                <label style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>
                  End Time
                </label>
                <input
                  type="datetime-local" step={1}
                  value={playbackEnd}
                  onChange={(e) => setPlaybackEnd(e.target.value)}
                  disabled={isPlaying}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    backgroundColor: '#1e293b',
                    color: '#e2e8f0',
                    border: '1px solid #334155',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: isPlaying ? 'not-allowed' : 'pointer',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
          )}

          {/* Animation CSS */}
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.6; }
            }
            
            @keyframes pulse-timeline {
              0%, 100% { 
                box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3), 0 2px 8px rgba(59, 130, 246, 0.8);
              }
              50% { 
                box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.15), 0 2px 12px rgba(59, 130, 246, 1);
              }
            }
          `}</style>
        </div>
      )}
    </div>
  );
});

EZOPENPlayer.displayName = 'EZOPENPlayer';

export default EZOPENPlayer;
